import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listMotorex,
  getMotorexCategories,
  getMotorexStats,
  getMotorexAbraBezProtejsku,
} from "../lib/api.js";
import type {
  MotorexListItem,
  MotorexStatsResponse,
  MotorexAbraBezProtejskuResponse,
} from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Button } from "../ui/Button.js";
import { Badge } from "../ui/Badge.js";
import { PackageIcon, HashIcon, AlertIcon } from "../lib/icons.js";

/**
 * MOTOREX — co výrobce vede × co z toho vedeme my.
 *
 * ⛔ TAHLE STRÁNKA NEŘÍDÍ ESHOP. Co prodáváme a za kolik, určuje ABRA;
 * na eshop to vozí `motorex_zapis.py` v repu `ktm-vlasim`. Do 15. 8. 2026
 * tu byla schvalovačka „Schválit na eshop" — zbytek plánu, který se v půlce
 * opustil. Neprošla jí ani jedna položka a tlačítka nic nedělala.
 *
 * K čemu stránka JE: vidět, co z katalogu výrobce už vedeme a co ne — odtud
 * se bere nápad, co začít prodávat, a co u výrobce nově přibylo nebo zmizelo.
 *
 * 🔑 Páruje se přes KÓD VÝROBCE, který ABRA nese v názvu („MOTOREX 013815
 * CROSS POWER 2T 1L"). ⛔ `ITEM_ID` z feedu jím není — je to interní ID
 * PrestaShopu. Kód se čte ze stránky výrobce; z EAN jde odvodit jen na
 * 82,7 % (změřeno na 81 kusech), proto je EAN označený jako odhad.
 *
 * ⛔ Ceny „z feedu" jsou SROVNÁVAČOVÉ a bývají nižší než pultovní — u osmi
 * ověřených kusů byly nižší u všech (Air Filter Oil Spray 395 × 449).
 * Podle nich se 13. 8. přepsalo 31 živých cen dolů. Proto se ukazuje
 * přednostně pultovní cena ze stránky.
 */

// 🔑 Dvě různé otázky, ne jedna: „vedeme" je o KARTĚ V ABŘE, „na eshopu"
// o tom, jestli to zákazník vidí. Karta v ABŘE ještě neznamená, že se to
// prodává (19. 8.: 81 vedeme × 66 na eshopu).
type FilterKey =
  | "vse" | "vedeme" | "nevedeme" | "neurceno" | "undecided"
  | "na-eshopu" | "skryto" | "mimo";

export function MotorexPage(): JSX.Element {
  const [categories, setCategories] = useState<Array<{ name: string; count: number }> | null>(null);
  const [stats, setStats] = useState<MotorexStatsResponse | null>(null);
  const [filter, setFilter] = useState<FilterKey>("vse");
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<MotorexListItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [bezProtejsku, setBezProtejsku] = useState<MotorexAbraBezProtejskuResponse | null>(null);
  const [ukazBezProtejsku, setUkazBezProtejsku] = useState(false);
  const LIMIT = 48;

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setOffset(0);
  }, [filter, category, debouncedSearch]);

  useEffect(() => {
    getMotorexCategories()
      .then((d) => setCategories(d.categories))
      .catch(() => setCategories([]));
    getMotorexStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, []);

  const loadItems = useCallback(() => {
    setLoading(true);
    return listMotorex({
      category: category ?? undefined,
      search: debouncedSearch || undefined,
      scope: filter === "undecided" ? "undecided" : undefined,
      stocked:
        filter === "vedeme" || filter === "nevedeme" || filter === "neurceno"
          ? filter
          : undefined,
      eshop:
        filter === "na-eshopu" || filter === "skryto" || filter === "mimo"
          ? filter
          : undefined,
      limit: LIMIT,
      offset,
    })
      .then((d) => {
        setItems(d.items);
        setTotal(d.total);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [category, debouncedSearch, filter, offset]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  function prepniBezProtejsku(): void {
    const dalsi = !ukazBezProtejsku;
    setUkazBezProtejsku(dalsi);
    if (dalsi && !bezProtejsku) {
      getMotorexAbraBezProtejsku()
        .then(setBezProtejsku)
        .catch(() => setBezProtejsku({ total: 0, items: [] }));
    }
  }

  if (!categories) return <LoadingBlock label="Načítám katalog…" />;

  const p = stats?.prehled;
  const filtry: Array<{ key: FilterKey; label: string; count?: number | undefined }> = [
    { key: "vse", label: "Vše", count: p?.vKatalogu },
    { key: "vedeme", label: "Vedeme", count: p?.vedeme },
    { key: "nevedeme", label: "Nevedeme", count: p?.nevedeme },
    ...(p && p.neurceno > 0
      ? [{ key: "neurceno" as FilterKey, label: "Bez kódu", count: p.neurceno }]
      : []),
    ...(p && p.noveKRozhodnuti > 0
      ? [{ key: "undecided" as FilterKey, label: "K rozhodnutí", count: p.noveKRozhodnuti }]
      : []),
    // Stav vůči eshopu — do 19. 8. se dal zjistit jen otevřením detailu.
    { key: "na-eshopu", label: "Na eshopu", count: p?.naEshopu },
    // „Skryté" jen když nějaké jsou — prázdný chip je šum (dnes 0).
    ...(p && p.skryto > 0
      ? [{ key: "skryto" as FilterKey, label: "Skryté", count: p.skryto }]
      : []),
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">MOTOREX</h1>
        <p className="mt-1 text-sm text-zinc-500">
          Co výrobce vede a co z toho vedeme my. <strong>Sortiment i ceny určuje ABRA</strong> —
          tenhle výpis je zrcadlo katalogu výrobce, ne řízení eshopu. Páruje se přes kód
          výrobce, který má ABRA v názvu položky.
        </p>
      </header>

      {/* Čtyři čísla, kvůli kterým stránka existuje */}
      <div className="mb-5 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Výrobce vede"
          value={p?.vKatalogu ?? 0}
          hint="v našem rozsahu — moto a ručně schválené"
        />
        <StatTile
          label="Z toho vedeme my"
          value={p?.vedeme ?? 0}
          tone="green"
          hint="spárováno na kartu v ABŘE"
          onClick={() => setFilter("vedeme")}
        />
        <StatTile
          label="Mohli bychom začít"
          value={p?.nevedeme ?? 0}
          tone="amber"
          hint="výrobce to má, my kartu nemáme"
          onClick={() => setFilter("nevedeme")}
        />
        <StatTile
          label="Nově přibylo"
          value={p?.noveKRozhodnuti ?? 0}
          tone={p && p.noveKRozhodnuti > 0 ? "red" : "neutral"}
          hint="nikdo o tom ještě nerozhodl"
          {...(p && p.noveKRozhodnuti > 0
            ? { onClick: () => setFilter("undecided") }
            : {})}
        />
      </div>

      {/* Poctivost: kde čísla stojí na odhadu */}
      {p && (p.neurceno > 0 || p.kodZeStranky < p.vKatalogu) && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <AlertIcon size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            {p.kodZeStranky < p.vKatalogu && (
              <>
                Kód výrobce je přečtený ze stránky u{" "}
                <strong>
                  {p.kodZeStranky} z {p.vKatalogu}
                </strong>{" "}
                položek. U zbytku je odvozený z EAN, což sedí na 82,7 % — čísla nahoře se
                proto ještě můžou o pár kusů pohnout.{" "}
              </>
            )}
            {p.neurceno > 0 && (
              <>
                U <strong>{p.neurceno}</strong> položek kód neznáme vůbec, takže nejsou ani
                ve „vedeme", ani v „mohli bychom".
              </>
            )}
          </div>
        </div>
      )}

      {/* Co vedeme, ale výrobce to ve feedu nemá */}
      {p && p.abraBezProtejsku > 0 && (
        <div className="mb-5 rounded-xl border border-zinc-200 bg-white px-4 py-3">
          <div className="flex items-center gap-2 flex-wrap text-sm text-zinc-700">
            <PackageIcon size={16} className="text-zinc-400 shrink-0" />
            <span>
              <strong>{p.abraBezProtejsku}</strong> karet Motorexu v ABŘE nemá ve feedu
              protějšek — sudy, zboží, které Motorex není (Loctite, trychtýř), a kusy, které
              výrobce možná přestal vést.
            </span>
            <button
              type="button"
              onClick={prepniBezProtejsku}
              className="underline font-medium text-zinc-900"
            >
              {ukazBezProtejsku ? "Skrýt" : "Vypsat je"}
            </button>
          </div>

          {ukazBezProtejsku && (
            <div className="mt-3 overflow-x-auto">
              {!bezProtejsku ? (
                <LoadingBlock label="Načítám…" />
              ) : (
                <table className="w-full text-sm min-w-[540px]">
                  <thead>
                    <tr className="text-left text-xs text-zinc-500 border-b border-zinc-200">
                      <th className="py-2 pr-3 font-medium">Kód ABRA</th>
                      <th className="py-2 pr-3 font-medium">Název</th>
                      <th className="py-2 pr-3 font-medium text-right">Skladem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bezProtejsku.items.map((r) => (
                      <tr key={r.abraCode} className="border-b border-zinc-100 last:border-0">
                        <td className="py-1.5 pr-3 font-mono text-xs text-zinc-500">
                          {r.abraCode}
                        </td>
                        <td className="py-1.5 pr-3 text-zinc-800">{r.name}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">
                          {r.qtyOnHand > 0 ? (
                            <span className="font-medium text-emerald-700">{r.qtyOnHand}</span>
                          ) : (
                            <span className="text-zinc-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {stats?.lastRun && (
        <p className="mb-4 text-xs text-zinc-400">
          Poslední import {new Date(stats.lastRun.startedAt).toLocaleString("cs-CZ")} — z{" "}
          {stats.lastRun.productsSeen.toLocaleString("cs-CZ")} položek feedu vzato{" "}
          {stats.lastRun.productsInScope.toLocaleString("cs-CZ")}, nových{" "}
          {stats.lastRun.productsNew}, změněných {stats.lastRun.productsUpdated}
          {stats.lastRun.productsVanished > 0
            ? `, zmizelo ${stats.lastRun.productsVanished}`
            : ""}
          .
          {stats.strankyCteneNaposled && (
            <>
              {" "}
              Stránky výrobce čtené naposled{" "}
              {new Date(stats.strankyCteneNaposled).toLocaleString("cs-CZ")}.
            </>
          )}
        </p>
      )}

      {/* Filtry */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {filtry.map((f) => {
          const isActive = filter === f.key;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap border transition-colors ${
                isActive
                  ? "bg-ktm-orange text-white border-ktm-orange"
                  : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-300"
              }`}
            >
              <span className="font-medium">{f.label}</span>
              {f.count != null && (
                <span className={`ml-2 text-xs ${isActive ? "text-white/80" : "text-zinc-400"}`}>
                  {f.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mb-6 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <HashIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Hledat název, kód výrobce nebo EAN…"
            className="h-11 sm:h-9 pl-9 pr-3 rounded-md border border-zinc-200 text-sm focus:border-ktm-orange focus:ring-2 focus:ring-ktm-orange-100 focus:outline-none w-full sm:w-80"
          />
        </div>
        <select
          value={category ?? ""}
          onChange={(e) => setCategory(e.target.value || null)}
          className="h-9 px-3 rounded-md border border-zinc-200 text-sm bg-white max-w-xs"
        >
          <option value="">Všechny kategorie</option>
          {categories.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({c.count})
            </option>
          ))}
        </select>
        <div className="ml-auto text-sm text-zinc-500">
          {total.toLocaleString("cs-CZ")} položek
        </div>
      </div>

      {loading && !items ? (
        <LoadingBlock label="Načítám…" />
      ) : !items || items.length === 0 ? (
        <EmptyState
          icon={<PackageIcon size={28} />}
          title="Žádné výsledky"
          description="Uprav filtr nebo hledání."
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {items.map((it) => (
            <MotorexCard key={it.id} item={it} />
          ))}
        </div>
      )}

      {total > LIMIT && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            ← Předchozí
          </Button>
          <div className="text-xs text-zinc-500">
            {offset + 1}–{Math.min(offset + LIMIT, total)} z {total.toLocaleString("cs-CZ")}
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Další →
          </Button>
        </div>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  tone = "neutral",
  hint,
  onClick,
}: {
  label: string;
  value: number;
  tone?: "neutral" | "green" | "amber" | "red";
  hint?: string;
  onClick?: () => void;
}): JSX.Element {
  const toneClass =
    tone === "green"
      ? "text-emerald-700"
      : tone === "amber"
        ? "text-amber-700"
        : tone === "red"
          ? "text-red-700"
          : "text-zinc-900";
  const obsah = (
    <>
      <div className="text-xs text-zinc-500">{label}</div>
      <div className={`mt-0.5 text-2xl font-semibold ${toneClass}`}>
        {value.toLocaleString("cs-CZ")}
      </div>
      {hint && <div className="mt-1 text-[11px] leading-snug text-zinc-400">{hint}</div>}
    </>
  );
  const zaklad = "rounded-xl border border-zinc-200 bg-white px-4 py-3 text-left";
  return onClick ? (
    <button
      type="button"
      onClick={onClick}
      className={`${zaklad} transition-colors hover:border-ktm-orange`}
    >
      {obsah}
    </button>
  ) : (
    <div className={zaklad}>{obsah}</div>
  );
}

/**
 * Fotka výrobce s náhradou, když se nenačte.
 *
 * 🔴 Nutné, protože feed veze u části položek MRTVOU adresu (VALVE GUARD →
 * `/2217/valve-guard.jpg` vrací 403). Bez tohohle prohlížeč vykreslí rozbitou
 * ikonu s `alt` textem přes celou dlaždici a výpis vypadá jako porucha.
 * Živou adresu doplní `motorex:scrape` ze stránky výrobce — do té doby
 * (a u kusů, kde fotka opravdu chybí) je vidět placeholder.
 */
export function FotkaVyrobce({
  src,
  alt,
  className,
  ikona = 40,
}: {
  src: string | null;
  alt: string;
  className?: string;
  ikona?: number;
}): JSX.Element {
  const [rozbita, setRozbita] = useState(false);
  if (!src || rozbita) {
    return (
      <div className="w-full h-full flex items-center justify-center text-zinc-300">
        <PackageIcon size={ikona} />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onError={() => setRozbita(true)}
      className={className}
    />
  );
}

/** Štítek „vedeme / nevedeme". `null` = kód neznáme, nedá se říct ani jedno. */
function stockedBadge(stocked: boolean | null): {
  label: string;
  tone: "green" | "amber" | "neutral";
} {
  if (stocked === true) return { label: "Vedeme", tone: "green" };
  if (stocked === false) return { label: "Nevedeme", tone: "amber" };
  return { label: "Bez kódu", tone: "neutral" };
}

/**
 * Štítek stavu vůči eshopu. Do 19. 8. 2026 byl jen na DETAILU, takže se
 * v mřížce nedalo poznat, co je venku a co schované (Vít 19. 8.).
 * Hodnoty odpovídají `eshopStav()` v `_ovladani.ts`.
 */
function EshopBadge({
  stav,
  obchod,
}: {
  stav: MotorexListItem["eshopStav"];
  /**
   * 🔀 Značka obchodu (28. 8. 2026). Vynechá se, když je obchod jediný —
   * dokud Triumph neběží, vypadá dlaždice přesně jako dřív.
   */
  obchod?: string;
}): JSX.Element | null {
  const p = obchod ? `${obchod}: ` : "";
  if (stav === "na-eshopu") return <Badge tone="green">{p}Na eshopu</Badge>;
  if (stav === "ceka") return <Badge tone="blue">{p}Čeká na robota</Badge>;
  if (stav === "chyba") return <Badge tone="red">{p}Zakládání selhalo</Badge>;
  if (stav === "skryto") return <Badge tone="neutral">{p}Skryto</Badge>;
  // „mimo" = na eshopu nikdy nebylo. U jediného obchodu je prázdno čitelnější;
  // u dvou by ale zmizel celý řádek a nešlo by poznat, KTERÝ obchod chybí.
  return obchod ? <Badge tone="neutral">{p}—</Badge> : null;
}

function MotorexCard({ item }: { item: MotorexListItem }): JSX.Element {
  const badge = stockedBadge(item.stocked);
  const skladem = item.abra.reduce((s, a) => s + a.qtyOnHand, 0);
  // Pultovní cena ze stránky je pravdivá; z feedu je srovnávačová a nižší.
  const cena = item.priceRetail ?? item.priceGross;
  const cenaZeStranky = item.priceRetail != null;

  return (
    <div className="group bg-white rounded-2xl border border-zinc-200 hover:border-ktm-orange hover:shadow-md overflow-hidden transition-all">
      <div className="aspect-square bg-zinc-50 relative overflow-hidden">
        <Link to={`/motorex/${encodeURIComponent(item.itemId)}`} className="block w-full h-full">
          <FotkaVyrobce
            src={item.imgUrl}
            alt={item.name}
            className="w-full h-full object-contain p-4 group-hover:scale-[1.03] transition-transform"
          />
        </Link>
        <div className="absolute bottom-2 left-2">
          <Badge tone={badge.tone}>{badge.label}</Badge>
        </div>
        {/* ⛔ Dřív tu byl POČET („u výrobce 999"). Motorex ho nezveřejňuje
            (`show_quantities: false`) a hodnota byla výplň jejich systému —
            988 kusů sudu 60 l za 30 540 Kč. Od 19. 8. 2026 se ukazuje SLOVO,
            což je jediné, co o dostupnosti opravdu říkají. */}
        {item.supplierInStock != null && (
          <div
            className={
              "absolute top-2 right-2 rounded-md bg-white/90 border px-1.5 py-0.5 text-[11px] " +
              (item.supplierInStock
                ? "border-emerald-200 text-emerald-700"
                : "border-red-200 text-red-700")
            }
          >
            {item.supplierInStock ? "u výrobce skladem" : "u výrobce není"}
          </div>
        )}
        <div className="absolute bottom-2 right-2 flex flex-col items-end gap-1">
          {/* 🔀 Dva obchody vedle sebe (28. 8. 2026). Dokud Triumph neběží,
              přijde `eshopStavTriumph: null` a kreslí se JEDEN štítek bez
              značky — tedy přesně to, co tu bylo do teď. */}
          {item.eshopStavTriumph === null ? (
            <EshopBadge stav={item.eshopStav} />
          ) : (
            <>
              <EshopBadge stav={item.eshopStav} obchod="KTM" />
              <EshopBadge stav={item.eshopStavTriumph} obchod="TR" />
            </>
          )}
        </div>
      </div>

      <Link to={`/motorex/${encodeURIComponent(item.itemId)}`} className="block p-3">
        <div className="text-xs text-zinc-400 font-mono truncate">
          {item.manufacturerCode ? (
            <span title={item.manufacturerCodeSource === "ean" ? "odhad z EAN" : "ze stránky výrobce"}>
              {item.manufacturerCode}
              {item.manufacturerCodeSource === "ean" && (
                <span className="text-amber-500"> ~</span>
              )}
            </span>
          ) : (
            <span className="text-zinc-300">bez kódu</span>
          )}
          {item.categoryText && (
            <>
              <span className="text-zinc-300"> · </span>
              <span>{item.categoryText.replace(/^MOTO \| /, "")}</span>
            </>
          )}
        </div>
        <div className="mt-1 text-sm font-medium text-zinc-900 line-clamp-2 group-hover:text-ktm-orange transition-colors min-h-[2.5rem]">
          {item.name}
        </div>

        <div className="mt-2 flex items-end justify-between gap-2">
          <div className="text-sm font-semibold text-zinc-900">
            {cena != null
              ? `${cena.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč`
              : "—"}
            <span className="ml-1 text-[11px] font-normal text-zinc-500">
              {cenaZeStranky ? "u výrobce" : "srovnávač"}
            </span>
          </div>
          {item.stocked === true && (
            <div className="text-[11px] text-right text-zinc-500 leading-tight">
              <div className="font-mono">{item.abra[0]?.abraCode}</div>
              <div>
                {skladem > 0 ? (
                  <span className="text-emerald-700 font-medium">{skladem} ks</span>
                ) : (
                  "0 ks"
                )}
              </div>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}
